import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';

import client from "../../utils/client";
import Alert from "../../utils/alert";
import errorToStr from "../../utils/errorToStr";
import Spinner from "../common/Spinner";
import Pagination from "../common/Pagination";
import { getUserList } from "../../actions/adminActions";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    fontSize: '1.4rem',
    '& input[type="checkbox"]': {
      marginRight: theme.spacing(1),
      width: '1.6rem',
      height: '1.6rem',
    },
  },
  adminUserList: {
    marginBottom: theme.spacing(3),
  },
  adminTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '1.3rem',
    '& th, & td': {
      border: `1px solid ${theme.palette.grey[300]}`,
      padding: theme.spacing(1),
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: theme.palette.grey[100],
      fontWeight: 600,
      fontSize: '1.4rem',
    },
    '& tr:nth-child(even)': {
      backgroundColor: theme.palette.grey[50],
    },
    '& tr:hover': {
      backgroundColor: theme.palette.grey[200],
    },
  },
  adminUserList__item: {
    '&:hover': {
      backgroundColor: theme.palette.grey[200],
    },
  },
  hasTextSuccess: {
    color: theme.palette.success.main,
    fontWeight: 500,
  },
  hasTextDanger: {
    color: theme.palette.error.main,
    fontWeight: 500,
  },
  button: {
    padding: theme.spacing(0.5, 1),
    fontSize: '1.2rem',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    textTransform: 'none',
    '&.is-small': {
      padding: theme.spacing(0.25, 0.75),
      fontSize: '1.1rem',
    },
    '&.is-primary': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
      '&:disabled': {
        backgroundColor: theme.palette.grey[300],
        color: theme.palette.grey[500],
        cursor: 'not-allowed',
      },
    },
    '&.is-danger': {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.error.dark,
      },
      '&:disabled': {
        backgroundColor: theme.palette.grey[300],
        color: theme.palette.grey[500],
        cursor: 'not-allowed',
      },
    },
  },
}));

const DEFAULT_PAGE_SIZE = 50;

const AdminUserList = props => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [currentPage, setPage] = useState(1);
  const [filter, setFilter] = useState({ unverified: false });

  const dispatch = useDispatch();
  const userList = useSelector(state => state.admin.userList);
  const itemCount = useSelector(state => state.admin.userCount);

  useEffect(() => {
    setLoading(true);
    dispatch(getUserList(currentPage, DEFAULT_PAGE_SIZE, filter));
  }, [currentPage, filter, setLoading, dispatch]);

  useEffect(() => {
    setLoading(false);
  }, [userList, setLoading]);

  const handlePageClick = selectedPage => {
    setPage(selectedPage);
  };

  const handleVerifiedCheckChange = () => {
    let newFilter = { ...filter };
    newFilter.unverified = !filter.unverified;
    setFilter(newFilter);
    setPage(1);
  };

  const handleDeleteUser = id => {
    Alert.confirm("このユーザーを削除しますか？").then(result => {
      if (result.value) {
        setLoading(true);
        client
          .delete("admin/users/" + id)
          .then(response => {
            Alert.success("削除しました");
            dispatch(getUserList(currentPage, DEFAULT_PAGE_SIZE, filter));
          })
          .catch(error => {
            Alert.error(errorToStr(error));
          })
          .finally(() => {
            setLoading(false);
          });
      }
    });
  };

  const handleBlockUser = id => {
    Alert.confirm("このユーザーを Block しますか？").then(result => {
      if (result.value) {
        setLoading(true);
        client
          .patch("admin/blockuser/" + id)
          .then(response => {
            Alert.success("Block しました");
            dispatch(getUserList(currentPage, DEFAULT_PAGE_SIZE, filter));
          })
          .catch(error => {
            Alert.error(errorToStr(error));
          })
          .finally(() => {
            setLoading(false);
          });
      }
    });
  };

  const handleVerifyUser = id => {
    client
      .patch("admin/business/" + id + "/verify")
      .then(response => {
        Alert.success("承認 しました");
        dispatch(getUserList(currentPage, DEFAULT_PAGE_SIZE, filter));
      })
      .catch(error => {
        Alert.error(errorToStr(error));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const renderUserList = () => {
    if (userList.length === 0)
      return (
        <tr>
          <td>No data</td>
        </tr>
      );

    return userList.map(user => {
      return (
        <tr key={user._id} className={classes.adminUserList__item}>
          <th>{user.name}</th>
          <td>
            {user.profile.user_type === "Business" ? (
              <span className={classes.hasTextSuccess}>{user.profile.user_type}</span>
            ) : (
              user.profile.user_type
            )}
          </td>
          <td>{user.email}</td>
          <td>
            {user.verified ? (
              <span>verified</span>
            ) : (
              <span className={classes.hasTextDanger}>unverified</span>
            )}
          </td>
          <td>
            {user.is_active ? (
              <span>active</span>
            ) : (
              <span className={classes.hasTextDanger}>inactive</span>
            )}
          </td>
          <td>{user.likedUsers ? user.likedUsers.length : 0}</td>
          <td>{user.created_at}</td>
          <td>
            <button
              className={`${classes.button} is-small is-primary`}
              onClick={() => handleVerifyUser(user._id)}
              disabled={
                !user.profile.user_type === "Business" ||
                  user.profile.companyVerified
                  ? "disabled"
                  : null
              }
            >
              承認
            </button>
          </td>
          <td>
            <button
              className={`${classes.button} is-small is-danger`}
              onClick={() => handleDeleteUser(user._id)}
              disabled={user.verified ? "disabled" : null}
            >
              削除
            </button>
          </td>
          <td>
            <button
              className={`${classes.button} is-small is-danger`}
              onClick={() => handleBlockUser(user._id)}
              disabled={!user.verified || !user.is_active ? "disabled" : null}
            >
              Block
            </button>
          </td>
        </tr>
      );
    });
  };

  return (
    <Box className={props.className}>
      <label className={classes.checkbox}>
        <input
          type="checkbox"
          checked={filter.unverified}
          onChange={handleVerifiedCheckChange}
        />
        Unverified user only
      </label>
      <div className={classes.adminUserList}>
        {loading ? (
          <Spinner />
        ) : (
          <table className={classes.adminTable}>
            <tbody>{renderUserList()}</tbody>
          </table>
        )}
      </div>
      <Pagination
        itemCount={itemCount}
        pageSize={DEFAULT_PAGE_SIZE}
        onPageChange={handlePageClick}
        className="bg-secondary-light"
      />
    </Box>
  );
};

export default AdminUserList;
